import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Smartphone, Key, QrCode, Copy, Check, AlertTriangle,
  Loader2, RefreshCw, Trash2, Monitor, Lock, Unlock, Eye, EyeOff
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TwoFactorSetup({ token, onComplete }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [setupStep, setSetupStep] = useState(0); // 0: initial, 1: qr shown, 2: verify, 3: backup codes
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showSecret, setShowSecret] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [trustedDevices, setTrustedDevices] = useState([]);
  
  // Disable dialog
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);
  
  // Regenerate backup codes dialog
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
      
      if (res.data.enabled) {
        // Fetch trusted devices
        const devicesRes = await axios.get(`${API}/api/auth/2fa/trusted-devices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrustedDevices(devicesRes.data.devices || []);
      }
    } catch (err) {
      console.error('Error fetching 2FA status:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupData(res.data);
      setSetupStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il setup');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndComplete = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Inserisci un codice di 6 cifre');
      return;
    }
    
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/api/auth/2fa/setup/complete`, 
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBackupCodes(res.data.backup_codes || []);
      setSetupStep(3);
      toast.success('2FA attivato con successo!');
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice non valido');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    if (!disableCode) {
      toast.error('Inserisci il codice di verifica');
      return;
    }
    
    setDisabling(true);
    try {
      await axios.post(`${API}/api/auth/2fa/disable`,
        { code: disableCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('2FA disattivato');
      setShowDisableDialog(false);
      setDisableCode('');
      fetchStatus();
      setSetupStep(0);
      setSetupData(null);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice non valido');
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regenerateCode) {
      toast.error('Inserisci il codice di verifica');
      return;
    }
    
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/api/auth/2fa/backup-codes/regenerate`,
        { code: regenerateCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBackupCodes(res.data.backup_codes || []);
      setShowRegenerateDialog(false);
      setRegenerateCode('');
      setSetupStep(3); // Show backup codes
      toast.success('Codici di backup rigenerati');
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice non valido');
    } finally {
      setRegenerating(false);
    }
  };

  const revokeDevice = async (devicePrefix) => {
    try {
      await axios.delete(`${API}/api/auth/2fa/trusted-devices/${devicePrefix}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dispositivo rimosso');
      fetchStatus();
    } catch (err) {
      toast.error('Errore nella rimozione');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
    toast.success('Copiato negli appunti');
  };

  const finishSetup = () => {
    setSetupStep(0);
    fetchStatus();
    if (onComplete) onComplete();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#86868B]" />
      </div>
    );
  }

  // 2FA is enabled - show management UI
  if (status?.enabled && setupStep !== 3) {
    return (
      <div className="space-y-6" data-testid="2fa-management">
        {/* Status Card */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <Shield className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-800">2FA Attivo</h3>
              <p className="text-green-600 text-sm">
                Il tuo account è protetto con autenticazione a due fattori
              </p>
            </div>
            <Badge className="ml-auto bg-green-100 text-green-800 border-0">
              {status.backup_codes_remaining} codici backup
            </Badge>
          </div>
        </div>

        {/* Trusted Devices */}
        <div className="bg-white rounded-2xl border border-[#E5E5EA] overflow-hidden">
          <div className="p-6 border-b border-[#E5E5EA]">
            <h3 className="text-lg font-semibold text-[#1D1D1F] flex items-center space-x-2">
              <Monitor className="h-5 w-5 text-[#86868B]" />
              <span>Dispositivi Fidati</span>
            </h3>
            <p className="text-sm text-[#86868B] mt-1">
              Questi dispositivi non richiedono la verifica 2FA
            </p>
          </div>
          
          {trustedDevices.length > 0 ? (
            <div className="divide-y divide-[#E5E5EA]">
              {trustedDevices.map((device, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Monitor className="h-5 w-5 text-[#86868B]" />
                    <div>
                      <p className="text-sm font-medium text-[#1D1D1F]">
                        {device.device_id}
                      </p>
                      <p className="text-xs text-[#86868B]">
                        IP: {device.ip_address || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revokeDevice(device.device_id.replace('...', ''))}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-[#86868B]">
              <Monitor className="h-12 w-12 mx-auto mb-3 text-[#C7C7CC]" />
              <p>Nessun dispositivo fidato</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            onClick={() => setShowRegenerateDialog(true)}
            className="flex-1"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rigenera Codici Backup
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowDisableDialog(true)}
            className="flex-1"
          >
            <Unlock className="h-4 w-4 mr-2" />
            Disattiva 2FA
          </Button>
        </div>

        {/* Disable Dialog */}
        <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                <span>Disattiva 2FA</span>
              </DialogTitle>
              <DialogDescription>
                Per disattivare l'autenticazione a due fattori, inserisci il codice dalla tua app o un codice di backup.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Codice di verifica</Label>
              <Input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="000000"
                className="mt-2 text-center text-2xl tracking-widest"
                maxLength={8}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
                Annulla
              </Button>
              <Button variant="destructive" onClick={handleDisable} disabled={disabling}>
                {disabling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Disattiva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Regenerate Dialog */}
        <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rigenera Codici Backup</DialogTitle>
              <DialogDescription>
                Inserisci il codice dalla tua app per generare nuovi codici di backup. I codici precedenti saranno invalidati.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Codice di verifica</Label>
              <Input
                value={regenerateCode}
                onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="mt-2 text-center text-2xl tracking-widest"
                maxLength={6}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRegenerateDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleRegenerateBackupCodes} disabled={regenerating}>
                {regenerating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Rigenera
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Setup flow
  return (
    <div className="space-y-6" data-testid="2fa-setup">
      {/* Step 0: Initial */}
      {setupStep === 0 && (
        <div className="text-center py-8">
          <div className="w-20 h-20 rounded-3xl bg-[#0071e3]/10 flex items-center justify-center mx-auto mb-6">
            <Shield className="h-10 w-10 text-[#0071e3]" />
          </div>
          <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-3">
            Proteggi il tuo account
          </h2>
          <p className="text-[#86868B] max-w-md mx-auto mb-8">
            Attiva l'autenticazione a due fattori per aggiungere un ulteriore livello di sicurezza al tuo account WEQASA.
          </p>
          <Button
            onClick={startSetup}
            className="h-12 px-8 rounded-full bg-[#0071e3] hover:bg-[#0077ED]"
            disabled={loading}
            data-testid="start-2fa-setup"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Lock className="h-5 w-5 mr-2" />}
            Attiva 2FA
          </Button>
        </div>
      )}

      {/* Step 1: QR Code */}
      {setupStep === 1 && setupData && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#0071e3]/10 flex items-center justify-center mx-auto mb-4">
              <QrCode className="h-8 w-8 text-[#0071e3]" />
            </div>
            <h2 className="text-xl font-semibold text-[#1D1D1F] mb-2">
              Scansiona il QR Code
            </h2>
            <p className="text-[#86868B] text-sm">
              Usa Google Authenticator, Authy o un'altra app TOTP
            </p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              <img
                src={`data:image/png;base64,${setupData.qr_code}`}
                alt="QR Code 2FA"
                className="w-48 h-48"
              />
            </div>
          </div>

          {/* Manual Entry */}
          <div className="bg-[#F5F5F7] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#1D1D1F]">
                Inserimento manuale
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <code className="flex-1 bg-white rounded-lg px-3 py-2 text-sm font-mono">
                {showSecret ? setupData.secret : '••••••••••••••••••••••••••••••••'}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(setupData.secret)}
              >
                {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <Button
            onClick={() => setSetupStep(2)}
            className="w-full h-12 rounded-full bg-[#0071e3] hover:bg-[#0077ED]"
          >
            Continua
          </Button>
        </div>
      )}

      {/* Step 2: Verify Code */}
      {setupStep === 2 && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#0071e3]/10 flex items-center justify-center mx-auto mb-4">
              <Key className="h-8 w-8 text-[#0071e3]" />
            </div>
            <h2 className="text-xl font-semibold text-[#1D1D1F] mb-2">
              Verifica il codice
            </h2>
            <p className="text-[#86868B] text-sm">
              Inserisci il codice a 6 cifre dalla tua app
            </p>
          </div>

          <div>
            <Input
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="text-center text-3xl tracking-[0.5em] h-16 rounded-xl"
              maxLength={6}
              autoFocus
              data-testid="2fa-verify-input"
            />
          </div>

          <div className="flex space-x-4">
            <Button
              variant="outline"
              onClick={() => setSetupStep(1)}
              className="flex-1 h-12 rounded-full"
            >
              Indietro
            </Button>
            <Button
              onClick={verifyAndComplete}
              className="flex-1 h-12 rounded-full bg-[#0071e3] hover:bg-[#0077ED]"
              disabled={verificationCode.length !== 6 || verifying}
              data-testid="2fa-verify-btn"
            >
              {verifying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Verifica
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Backup Codes */}
      {setupStep === 3 && backupCodes.length > 0 && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#1D1D1F] mb-2">
              2FA Attivato!
            </h2>
            <p className="text-[#86868B] text-sm">
              Salva questi codici di backup in un posto sicuro
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Importante</p>
                <p>Ogni codice può essere usato una sola volta. Conservali in modo sicuro per accedere al tuo account se perdi il telefono.</p>
              </div>
            </div>
          </div>

          <div className="bg-[#1D1D1F] rounded-2xl p-6">
            <div className="grid grid-cols-2 gap-3">
              {backupCodes.map((code, idx) => (
                <div
                  key={idx}
                  className="bg-white/10 rounded-lg px-4 py-2 text-center"
                >
                  <code className="text-white font-mono text-lg">{code}</code>
                </div>
              ))}
            </div>
            <Button
              variant="ghost"
              className="w-full mt-4 text-white hover:bg-white/10"
              onClick={() => copyToClipboard(backupCodes.join('\n'))}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copia tutti i codici
            </Button>
          </div>

          <Button
            onClick={finishSetup}
            className="w-full h-12 rounded-full bg-[#0071e3] hover:bg-[#0077ED]"
            data-testid="2fa-finish-btn"
          >
            Ho salvato i codici
          </Button>
        </div>
      )}
    </div>
  );
}
